import { createTypicalProject, projectEquipment } from '~/utils/projectCatalog';
import { DEFAULT_ARCHIVE, projectText } from '~/utils/project';
import { supportMonthlyPrice } from '~/utils/projectPricing';
import { formatPrice } from '~/utils/shop';
import { useEquipmentCatalog } from './useEquipmentCatalog';
import { useProjectPlanner } from './useProjectPlanner';
export type EstimateCameras = '4' | '8' | 'custom';
const profiles = {
  business: {
    title: 'Бизнес под контролем',
    zones: ['Вход', 'Касса', 'Торговый зал'],
    cameras: 'Камеры для помещений и входной группы',
  },
  home: {
    title: 'Небольшой дом под присмотром',
    zones: ['Вход', 'Комнаты', 'Двор'],
    cameras: 'Камеры внутри дома и снаружи у входа',
  },
  warehouse: {
    title: 'Обзор склада и территории',
    zones: ['Ворота', 'Разгрузка', 'Проходы'],
    cameras: 'Камеры для внутренних и уличных зон',
  },
};
export type EstimateObject = keyof typeof profiles;
export function useEstimate() {
  const selection = useState('project-estimate', () => ({
    object: 'business' as EstimateObject,
    cameras: '4' as EstimateCameras,
    includeArchive: true,
    includeRouter: false,
    service: false,
  }));
  const profile = computed(() =>
    selection.value.cameras === '8'
      ? {
          ...profiles[selection.value.object],
          zones: ['Помещения'],
          cameras: '8 камер для помещений',
        }
      : profiles[selection.value.object],
  );
  const archiveSummary = `Архив ${DEFAULT_ARCHIVE.days} дней · ${DEFAULT_ARCHIVE.hours} ч/сутки`;
  const { data: products, pending, error, refresh } = useEquipmentCatalog();
  const { commit } = useProjectPlanner();
  const draft = computed(() => {
    if (selection.value.cameras === 'custom') return null;
    return {
      ...createTypicalProject(selection.value.object, Number(selection.value.cameras) as 4 | 8),
      includeArchive: selection.value.includeArchive,
      includeRouter: selection.value.includeRouter,
      includeService: selection.value.service,
    };
  });
  const pack = computed(() =>
    draft.value ? projectEquipment(draft.value, error.value ? [] : products.value) : null,
  );
  const price = computed(() =>
    !pack.value
      ? 'По вашему проекту'
      : pending.value
        ? 'Обновляем цены…'
        : error.value
          ? 'Цены недоступны'
          : pack.value.total === null
            ? 'Нужен подбор'
            : formatPrice(pack.value.total),
  );
  const servicePrice = computed(() => {
    const monthly = supportMonthlyPrice(Number(selection.value.cameras));
    return monthly === null ? 'По отдельному расчёту' : formatPrice(monthly) + ' / мес.';
  });
  function typicalPrice(count: 4 | 8) {
    if (pending.value) return 'Загружаем цены…';
    if (error.value) return 'Цены недоступны';
    const quote = projectEquipment(createTypicalProject('business', count), products.value);
    return quote.total === null ? 'Нужен подбор' : formatPrice(quote.total);
  }
  function prepare() {
    if (draft.value) commit(draft.value);
  }
  function text() {
    const names: Record<string, string> = { business: 'Бизнес', home: 'Дом', warehouse: 'Склад' };
    return [
      'Robotics Vision — предварительный расчёт',
      '',
      `Объект: ${names[selection.value.object]}`,
      `Зоны наблюдения: ${profile.value.zones.join(', ')}`,
      `Подбор камер: ${profile.value.cameras}`,
      `Камеры: ${selection.value.cameras === 'custom' ? 'Уточнить при обследовании' : selection.value.cameras}`,
      `Режим: ${selection.value.includeArchive ? archiveSummary : 'Без архива, просмотр в реальном времени'}`,
      `Роутер: ${selection.value.includeRouter ? 'Добавить новый' : 'Использовать свой'}`,
      `Оборудование и монтаж: ${price.value}`,
      `Сопровождение: ${selection.value.service ? servicePrice.value : 'Не выбрано'}`,
      '',
      ...(draft.value
        ? [projectText(draft.value, pending.value || error.value ? undefined : pack.value!)]
        : ['Подберём оборудование после уточнения проекта.']),
    ].join('\n');
  }
  return {
    selection,
    profile,
    archiveSummary,
    pack,
    price,
    servicePrice,
    text,
    prepare,
    typicalPrice,
    pending,
    error,
    refresh,
  };
}
